import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import TaskModal from '../../components/TaskModal';
import ConfirmModal from '../../components/ConfirmModal';
import api from '../../services/api';

const ManagerProjectTasksPage = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchProjects();
    fetchUsers();
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectTasks(selectedProject.id);
    } else {
      fetchAllTasks();
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data.projects || response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects. Please try again.');
      setLoading(false);
    }
  };

  const fetchProjectTasks = async (projectId) => {
    try {
      setLoading(true);
      const response = await api.get(`/projects/${projectId}/tasks`);
      setTasks(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching project tasks:', err);
      setError('Failed to load tasks. Please try again.');
      setLoading(false);
    }
  };

  const fetchAllTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/manager/tasks');
      setTasks(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching all tasks:', err);
      setError('Failed to load tasks. Please try again.');
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users', {
        params: { limit: 1000 }
      });
      setUsers(response.data.users || response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const handleCreateTask = () => {
    setCurrentTask(null);
    setShowTaskModal(true);
  };

  const handleEditTask = (task) => {
    setCurrentTask(task);
    setShowTaskModal(true);
  };

  const handleDeleteTask = (task) => {
    setCurrentTask(task);
    setShowDeleteModal(true);
  };

  const handleSaveTask = async (taskData) => {
    try {
      // If a project is selected, assign the task to that project
      if (selectedProject && !taskData.project_id) {
        taskData.project_id = selectedProject.id;
      }
      
      if (currentTask) {
        // Update existing task
        await api.put(`/manager/tasks/${currentTask.id}`, taskData);
      } else {
        // Create new task
        await api.post('/manager/tasks', taskData);
      }
      setShowTaskModal(false);
      
      // Refresh tasks
      if (selectedProject) {
        fetchProjectTasks(selectedProject.id);
      } else {
        fetchAllTasks();
      }
    } catch (err) {
      console.error('Error saving task:', err);
      alert('Failed to save task. Please try again.');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/tasks/${currentTask.id}`);
      setShowDeleteModal(false);
      
      // Refresh tasks
      if (selectedProject) {
        fetchProjectTasks(selectedProject.id);
      } else {
        fetchAllTasks();
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      todo: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      testing: 'bg-purple-100 text-purple-800',
      client_review: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800'
    };
    
    const statusLabels = {
      todo: 'To Do',
      in_progress: 'In Progress',
      testing: 'Testing',
      client_review: 'Client Review',
      completed: 'Completed'
    };
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityClasses = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${priorityClasses[priority] || 'bg-gray-100 text-gray-800'}`}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </span>
    );
  };

  if (loading && projects.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Project Tasks</h1>
        <div className="text-center py-8">Loading...</div>
      </div>
    );
  }

  if (error && projects.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Project Tasks</h1>
        <div className="text-center py-8 text-red-500">{error}</div>
        <button 
          onClick={fetchProjects} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Project Tasks</h1>
        <button
          onClick={handleCreateTask}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
        >
          <FiPlus className="mr-2" /> Create Task
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Projects List */}
        <div className="w-full md:w-1/4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h2 className="text-lg font-medium text-gray-900">Projects</h2>
            </div>
            <ul className="divide-y divide-gray-200">
              <li>
                <button
                  onClick={() => setSelectedProject(null)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${!selectedProject ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
                >
                  All Tasks
                </button>
              </li>
              {projects.map(project => (
                <li key={project.id}>
                  <button
                    onClick={() => setSelectedProject(project)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${selectedProject?.id === project.id ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
                  >
                    {project.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Tasks List */}
        <div className="w-full md:w-3/4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h2 className="text-lg font-medium text-gray-900">
                {selectedProject ? `Tasks for ${selectedProject.name}` : 'All Tasks'}
              </h2>
            </div>
            
            {loading ? (
              <div className="text-center py-8">Loading tasks...</div>
            ) : error ? (
              <div className="text-center py-8 text-red-500">{error}</div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No tasks found. Click "Create Task" to add a new task.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Task
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Priority
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assigned To
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tasks.map(task => (
                      <tr key={task.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{task.title}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">{task.description ? task.description.replace(/<[^>]*>/g, ' ').trim() : ''}</div>
                          {task.Project && !selectedProject && (
                            <div className="text-xs text-gray-500 mt-1">
                              Project: {task.Project.name}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(task.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getPriorityBadge(task.priority)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatDate(task.due_date)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {task.Users && task.Users.length > 0
                              ? task.Users.map(user => user.name).join(', ')
                              : 'Unassigned'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleEditTask(task)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <FiEdit2 size={18} title="Edit Task" />
                            </button>
                            <button
                              onClick={() => handleDeleteTask(task)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <FiTrash2 size={18} title="Delete Task" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {showTaskModal && (
        <TaskModal
          task={currentTask}
          onClose={() => setShowTaskModal(false)}
          onSave={handleSaveTask}
          users={users}
          clients={clients}
          projects={projects}
        />
      )}

      {showDeleteModal && (
        <ConfirmModal
          title="Delete Task"
          message={`Are you sure you want to delete "${currentTask.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default ManagerProjectTasksPage;